import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { formatMoneyFull } from "@/lib/money";
import { dayUnitPrice } from "../utils/dayUnitPrice";
import type { PriceLevelInfo } from "./BottomUpDailyGrid";

interface AspCell {
  price: number;
  currency: string;
}

interface Props {
  dateIso: string;
  quantity: number;
  asp: AspCell | null;
  currency?: string | null;
  levelId: string | undefined;
  level: PriceLevelInfo | undefined;
  levelPrice: number | undefined;
  label?: string;
  children: React.ReactNode;
}

function dayLabel(iso: string): string {
  const [, m, d] = iso.split("-");
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}

export default function DayPricePopover({ dateIso, quantity, asp, currency, levelId, level, levelPrice, label, children }: Props) {
  const unit = dayUnitPrice(
    levelId !== undefined ? { [levelId]: levelPrice ?? 0 } : undefined,
    asp?.price,
    levelId,
  );
  const displayCurrency = asp?.currency ?? currency;
  const formatMoney = (amount: number) => formatMoneyFull(amount, displayCurrency);

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-64 text-sm tabular-nums">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold">{dayLabel(dateIso)}</span>
          {level ? (
            <span className="flex items-center gap-1 text-muted-foreground">
              <span
                aria-hidden
                style={{ background: level.color || "transparent" }}
                className="inline-block w-2 h-2 rounded-full"
              />
              {level.name}
            </span>
          ) : (
            <span className="text-muted-foreground">No price level</span>
          )}
        </div>
        {label && <div className="mt-1 text-muted-foreground">{label}</div>}
        <div className="mt-2">
          {unit?.source === "level" && (
            <>
              <div className="font-semibold">
                {level?.name ?? "Level"} price: {formatMoney(unit.price)}
              </div>
              {asp && <div className="text-muted-foreground">Target ASP: {formatMoney(asp.price)}</div>}
            </>
          )}
          {unit?.source === "asp" && (
            <>
              {level && (
                <div className="text-muted-foreground">{level.name}: no price set — using Target ASP</div>
              )}
              <div className="font-semibold">Target ASP: {formatMoney(unit.price)}</div>
            </>
          )}
          {!unit && (
            <div className="text-muted-foreground">
              No price — set Target ASP or level prices on Product Pricings
            </div>
          )}
        </div>
        {unit && quantity > 0 && (
          <div className="mt-2 text-muted-foreground">
            {formatMoney(quantity)} × {formatMoney(unit.price)} = {formatMoney(quantity * unit.price)}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
